import { IAssetLabel } from 'src/app/models/assetLabel';
import {
  LabelSchema,
  DimensionsSchema,
  WeightSchema,
  PackagingSchema,
  MaterialsSchema,
  IngredientsSchema,
  NutritionFactsSchema,
  EnvironmentalInfoSchema,
  UsageInstructionsSchema,
  BrandExtensionSchema,
  MarketingCopySchema,
  RegulatoryInfoSchema,
  IngredientDetailSchema,
  NutrientSchema,
} from 'src/app/models/label-schema';

// Field type for label editor
export type FieldType = 'primitive' | 'object' | 'primitiveArray' | 'objectArray';

// Label field structure used in the editor
export interface LabelField {
  key: string;
  path: string;
  type: FieldType;
  isReadonly: boolean;
  children?: LabelField[];
}

// Dialog input data
export interface LabelEditorDialogData {
  assetLabel: IAssetLabel;
  assetId: string;
  assetType: string;
}

// Helper to extract keys from schema interface
const keysOf = <T extends object>(...keys: (keyof T)[]): string[] => keys as string[];

// Schema subfields for each object type (derived from schema interfaces)
export const SCHEMA_SUBFIELDS: Record<string, string[]> = {
  dimensions: keysOf<DimensionsSchema>('height', 'width', 'depth', 'diameter', 'unit'),
  weight: keysOf<WeightSchema>('grossWeight', 'netWeight', 'unit'),
  packaging: keysOf<PackagingSchema>('count', 'volume', 'materials', 'recyclingLabels', 'inBox'),
  materials: keysOf<MaterialsSchema>('visible', 'inferred'),
  productMaterials: keysOf<MaterialsSchema>('visible', 'inferred'),
  ingredients: keysOf<IngredientsSchema>('rawText', 'items', 'allergens'),
  nutritionFacts: keysOf<NutritionFactsSchema>('servingSize', 'servingsPerContainer', 'nutrients', 'notes'),
  environmentalInfo: keysOf<EnvironmentalInfoSchema>('accreditationMarkings', 'environmentalClaims', 'animalWelfareClaims'),
  usageInstructions: keysOf<UsageInstructionsSchema>('storage', 'usage', 'assembly', 'care', 'preparation'),
  brandExtension: keysOf<BrandExtensionSchema>('subBrand', 'variantDescription', 'shortDescription', 'colourDescription', 'keywords', 'care', 'preparation'),
  // Object array item schemas (use distinct names to avoid conflict with array field names)
  marketingCopyItem: keysOf<MarketingCopySchema>('title', 'content', 'keyFeatures'),
  regulatoryInfoItem: keysOf<RegulatoryInfoSchema>('type', 'value', 'additionalDetails'),
  ingredientDetail: keysOf<IngredientDetailSchema>('name', 'quantityInfo', 'notes'),
  nutrient: keysOf<NutrientSchema>('name', 'dailyValue', 'perServingAmount', 'perServingUnit', 'referenceLabel', 'referenceAmount', 'referenceUnit', 'perReferenceAmount', 'perReferenceUnit', 'notes'),
  // Additional info fields (dictionary, not schema-typed)
  additionalInfo: ['notes', 'reference', 'source'],
};

// Fields that are primitive arrays (List<string>)
export const PRIMITIVE_ARRAY_FIELDS = new Set([
  ...keysOf<MaterialsSchema>('visible', 'inferred'),
  ...keysOf<PackagingSchema>('recyclingLabels'),
  ...keysOf<MarketingCopySchema>('keyFeatures'),
  ...keysOf<IngredientsSchema>('allergens'),
  ...keysOf<EnvironmentalInfoSchema>('accreditationMarkings', 'environmentalClaims', 'animalWelfareClaims'),
]);

// Fields that are object arrays (List<T> where T is an object)
export const OBJECT_ARRAY_FIELDS = new Set([
  ...keysOf<LabelSchema>('marketingCopy', 'regulatoryInfo'),
  ...keysOf<IngredientsSchema>('items'),
  ...keysOf<NutritionFactsSchema>('nutrients'),
]);

// Maps array field names to their item schema names
export const ARRAY_ITEM_SCHEMA: Record<string, string> = {
  items: 'ingredientDetail',
  nutrients: 'nutrient',
  marketingCopy: 'marketingCopyItem',
  regulatoryInfo: 'regulatoryInfoItem',
};

// Fields that should be stored as JSON strings
export const JSON_FIELDS = new Set<string>();

// Fields that are Dictionary<string, string> and allow custom keys
export const DICTIONARY_FIELDS = new Set([
  ...keysOf<LabelSchema>('additionalInfo'),
]);

// Fields that are objects (not arrays, not primitives)
export const OBJECT_FIELDS = new Set([
  ...keysOf<LabelSchema>('dimensions', 'weight', 'packaging', 'productMaterials', 'nutritionFacts', 'ingredients', 'environmentalInfo', 'usageInstructions', 'brandExtension'),
  ...keysOf<PackagingSchema>('materials'),
]);

// Fields that should be converted to numbers when saving
export const NUMERIC_FIELDS = new Set([
  ...keysOf<DimensionsSchema>('height', 'width', 'depth', 'diameter'),
  ...keysOf<WeightSchema>('grossWeight', 'netWeight'),
  ...keysOf<PackagingSchema>('count'),
  ...keysOf<LabelSchema>('schemaVersion'),
  ...keysOf<NutrientSchema>('perServingAmount', 'referenceAmount', 'perReferenceAmount'),
]);

// Fields that should be converted to booleans when saving
export const BOOLEAN_FIELDS = new Set([
  ...keysOf<PackagingSchema>('inBox'),
]);

// Fields that should be read-only
export const READONLY_FIELDS: (keyof LabelSchema)[] = ['schemaVersion', 'model'];
